import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.css';

const CardNumberHelp = () => {
  const dialogRef = useRef(null);
  const savedScrollY = useRef(0);
  const scrollLocked = useRef(false);

  const open = () => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    savedScrollY.current = window.scrollY;
    document.body.classList.add('noscroll');
    document.body.style.top = `${-savedScrollY.current}px`;
    scrollLocked.current = true;
    dialog.showModal();
    dialog.focus();
  };

  const close = () => {
    const dialog = dialogRef.current;
    if (dialog) dialog.close();
  };

  const handleDialogClick = (e) => {
    if (e.target === e.currentTarget) close();
  };

  const unlockScroll = () => {
    if (!scrollLocked.current) return;
    scrollLocked.current = false;
    document.body.classList.remove('noscroll');
    document.body.style.top = '';
  };

  const handleClose = () => {
    unlockScroll();
    window.scrollTo(0, savedScrollY.current);
  };

  // Release the scroll lock if unmounted while open (e.g. browser back)
  useEffect(() => unlockScroll, []);

  return (
    <>
      <button type="button" className="modal-card-number-launch" onClick={open}>
        <span className="visuallyhidden">Find out more</span>
      </button>
      {createPortal(
        <dialog
          className={`modal-card-number ${styles.dialog}`}
          ref={dialogRef}
          onClick={handleDialogClick}
          onClose={handleClose}
        >
          <div className="modal-card-number__inner">
            <div className="modal-card-number__header">
              <button type="button" className="modal-card-number__close" onClick={close}>
                <span className="visuallyhidden">Close</span>
              </button>
              <h3 className="modal-card-number__title">Where do I find my card number?</h3>
            </div>
            <div className="modal-card-number__body">
              <h3>Top right-hand corner</h3>
              <p>New licences show it on the back of the licence in the top right-hand corner.</p>
              <img
                src="/images/card-number/card-number-new-licence.jpg"
                width="274"
                height="154"
                alt="Card showing card number at top right on back"
                loading="lazy"
              />
              <h3>Below your date of birth</h3>
              <p>On some old cards you can find your card number in small text to the bottom right of the driver&#8217;s birth year.</p>
              <img
                src="/images/card-number/card-number-dob.png"
                width="257"
                height="156"
                alt="Card showing card number beneath the birth year on the back"
                loading="lazy"
              />
              <h3>On your digital driver licence</h3>
              <ol>
                <li>Open your licence in the <strong>VicRoads app</strong></li>
                <li>Scroll down to the bottom of the page</li>
                <li>Tap the &#8216;eye&#8217; icon to see your card number</li>
              </ol>
              <img
                src="/images/card-number/card-number-digital.jpg"
                width="134"
                height="214"
                alt="Digital licence indicating where to find card number"
                loading="lazy"
              />
              <h3>Online</h3>
              <p>On your <a href="https://www.vicroads.vic.gov.au/online-services/login" target="_blank" rel="noopener noreferrer">VicRoads account</a></p>
            </div>
            <div className="modal-card-number__footer">
              <button type="button" className="modal-card-number__back" onClick={close}>Back to form</button>
            </div>
          </div>
        </dialog>,
        document.body
      )}
    </>
  );
};

export default CardNumberHelp;
